/**
 * Centralized Error Handling Utility
 * Provides consistent error handling, reporting, and recovery across the application
 */

import { logger } from '../services/LoggingService';

export enum ErrorType {
  NETWORK = 'NETWORK',
  VALIDATION = 'VALIDATION',
  AUTHENTICATION = 'AUTHENTICATION',
  AUTHORIZATION = 'AUTHORIZATION',
  BUSINESS_LOGIC = 'BUSINESS_LOGIC',
  SYSTEM = 'SYSTEM',
  UNKNOWN = 'UNKNOWN'
}

export enum ErrorSeverity {
  LOW = 'LOW',
  MEDIUM = 'MEDIUM',
  HIGH = 'HIGH',
  CRITICAL = 'CRITICAL'
}

export interface ErrorContext {
  userId?: string;
  sessionId?: string;
  operation?: string;
  component?: string;
  metadata?: Record<string, unknown>;
}

export interface TechnicalDetails {
  name?: string;
  stack?: string;
  response?: {
    status?: number;
    statusText?: string;
    data?: unknown;
  };
  [key: string]: unknown;
}

export interface AppError extends Error {
  type: ErrorType;
  severity: ErrorSeverity;
  code?: string;
  context?: ErrorContext;
  timestamp: Date;
  recoverable: boolean;
  userMessage?: string;
  technicalDetails?: TechnicalDetails;
}

export class ApplicationError extends Error implements AppError {
  type: ErrorType;
  severity: ErrorSeverity;
  code?: string;
  context?: ErrorContext;
  timestamp: Date;
  recoverable: boolean;
  userMessage?: string;
  technicalDetails?: TechnicalDetails;

  constructor(
    message: string,
    type: ErrorType = ErrorType.UNKNOWN,
    severity: ErrorSeverity = ErrorSeverity.MEDIUM,
    options?: {
      code?: string;
      context?: ErrorContext;
      recoverable?: boolean;
      userMessage?: string;
      technicalDetails?: TechnicalDetails;
    }
  ) {
    super(message);
    this.name = 'ApplicationError';
    this.type = type;
    this.severity = severity;
    this.code = options?.code;
    this.context = options?.context;
    this.timestamp = new Date();
    this.recoverable = options?.recoverable ?? severity !== ErrorSeverity.CRITICAL;
    this.userMessage = options?.userMessage || this.getDefaultUserMessage(type);
    this.technicalDetails = options?.technicalDetails;

    // Maintain proper stack trace
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ApplicationError);
    }
  }

  private getDefaultUserMessage(type: ErrorType): string {
    const messages: Record<ErrorType, string> = {
      [ErrorType.NETWORK]: 'Network connection error. Please check your internet connection and try again.',
      [ErrorType.VALIDATION]: 'Please check your input and try again.',
      [ErrorType.AUTHENTICATION]: 'Authentication failed. Please sign in again.',
      [ErrorType.AUTHORIZATION]: 'You do not have permission to perform this action.',
      [ErrorType.BUSINESS_LOGIC]: 'Unable to complete the operation. Please try again.',
      [ErrorType.SYSTEM]: 'A system error occurred. Please try again later.',
      [ErrorType.UNKNOWN]: 'An unexpected error occurred. Please try again.'
    };
    return messages[type];
  }
}

export class ErrorHandler {
  private static instance: ErrorHandler;
  private errorCallbacks: ((error: AppError) => void)[] = [];
  private errorQueue: AppError[] = [];
  private isProcessing = false;

  private constructor() {}

  static getInstance(): ErrorHandler {
    if (!ErrorHandler.instance) {
      ErrorHandler.instance = new ErrorHandler();
    }
    return ErrorHandler.instance;
  }

  /**
   * Register a callback to be notified when errors occur
   */
  onError(callback: (error: AppError) => void): () => void {
    this.errorCallbacks.push(callback);
    // Return unsubscribe function
    return () => {
      this.errorCallbacks = this.errorCallbacks.filter(cb => cb !== callback);
    };
  }

  /**
   * Handle an error with appropriate logging and notifications
   */
  async handleError(error: Error | AppError, context?: ErrorContext): Promise<void> {
    const appError = this.normalizeError(error, context);
    
    // Add to queue for processing
    this.errorQueue.push(appError);
    
    // Process queue if not already processing
    if (!this.isProcessing) {
      await this.processErrorQueue();
    }
  }

  /**
   * Process queued errors
   */
  private async processErrorQueue(): Promise<void> {
    this.isProcessing = true;
    
    while (this.errorQueue.length > 0) {
      const error = this.errorQueue.shift()!;
      
      try {
        // Log the error
        this.logError(error);
        
        // Report to monitoring service (if configured)
        await this.reportError(error);
        
        // Notify subscribers
        this.notifySubscribers(error);
        
        // Handle recovery if possible
        await this.attemptRecovery(error);
        
      } catch (handlingError) {
        // Log meta-error but don't re-throw to prevent infinite loops
        logger.error('Error while handling error', 'ErrorHandler', {}, handlingError as Error);
      }
    }
    
    this.isProcessing = false;
  }

  /**
   * Normalize any error into AppError format
   */
  private normalizeError(error: Error | AppError, context?: ErrorContext): AppError {
    if (error instanceof ApplicationError) {
      if (context) {
        error.context = { ...error.context, ...context };
      }
      return error;
    }

    // Detect error type based on error characteristics
    const type = this.detectErrorType(error);
    const severity = this.determineSeverity(error, type);

    return new ApplicationError(
      error.message,
      type,
      severity,
      {
        context,
        technicalDetails: {
          name: error.name,
          stack: error.stack,
          ...(((error as any).response) ? { 
            response: (error as any).response 
          } : {})
        }
      }
    );
  }

  /**
   * Detect error type from error characteristics
   */
  private detectErrorType(error: Error): ErrorType {
    const message = error.message.toLowerCase();
    const name = error.name.toLowerCase();

    if (name.includes('network') || message.includes('fetch') || message.includes('network')) {
      return ErrorType.NETWORK;
    }
    if (name.includes('validation') || message.includes('invalid') || message.includes('required')) {
      return ErrorType.VALIDATION;
    }
    if (name.includes('auth') || message.includes('unauthorized') || message.includes('401')) {
      return ErrorType.AUTHENTICATION;
    }
    if (message.includes('forbidden') || message.includes('403') || message.includes('permission')) {
      return ErrorType.AUTHORIZATION;
    }
    if (name.includes('business') || message.includes('constraint') || message.includes('rule')) {
      return ErrorType.BUSINESS_LOGIC;
    }
    if (name.includes('system') || name.includes('internal')) {
      return ErrorType.SYSTEM;
    }

    return ErrorType.UNKNOWN;
  }

  /**
   * Determine error severity
   */
  private determineSeverity(error: Error, type: ErrorType): ErrorSeverity {
    // Network errors are usually medium severity
    if (type === ErrorType.NETWORK) {
      return ErrorSeverity.MEDIUM;
    }
    
    // Validation errors are low severity
    if (type === ErrorType.VALIDATION) {
      return ErrorSeverity.LOW;
    }
    
    // Auth errors are high severity
    if (type === ErrorType.AUTHENTICATION || type === ErrorType.AUTHORIZATION) {
      return ErrorSeverity.HIGH;
    }
    
    // System errors are critical
    if (type === ErrorType.SYSTEM) {
      return ErrorSeverity.CRITICAL;
    }

    // Default to medium
    return ErrorSeverity.MEDIUM;
  }

  /**
   * Log error with appropriate level
   */
  private logError(error: AppError): void {
    const logData = {
      message: error.message,
      type: error.type,
      severity: error.severity,
      code: error.code,
      context: error.context,
      timestamp: error.timestamp,
      stack: error.stack
    };

    switch (error.severity) {
      case ErrorSeverity.LOW:
        logger.warn('Application error', 'ErrorHandler', logData);
        break;
      case ErrorSeverity.MEDIUM:
        logger.error('Application error', 'ErrorHandler', logData);
        break;
      case ErrorSeverity.HIGH:
      case ErrorSeverity.CRITICAL:
        logger.error('Critical application error', 'ErrorHandler', logData);
        break;
    }
  }

  /**
   * Report error to monitoring service
   */
  private async reportError(error: AppError): Promise<void> {
    // In production, this would send to Sentry, LogRocket, etc.
    if (process.env.NODE_ENV === 'production') {
      // TODO: Implement actual error reporting service integration
      logger.error('Error reporting to monitoring service', 'ErrorHandler', {
        errorType: error.type,
        severity: error.severity,
        code: error.code
      }, error);
    }
  }

  /**
   * Notify all registered error callbacks
   */
  private notifySubscribers(error: AppError): void {
    this.errorCallbacks.forEach(callback => {
      try {
        callback(error);
      } catch (callbackError) {
        logger.error('Error in error callback', 'ErrorHandler', {}, callbackError as Error);
      }
    });
  }

  /**
   * Attempt automatic recovery for certain error types
   */
  private async attemptRecovery(error: AppError): Promise<void> {
    if (!error.recoverable) {
      return;
    }

    switch (error.type) {
      case ErrorType.NETWORK:
        // Could implement retry logic here
        logger.info('Network error detected, recovery may involve retry', 'ErrorHandler');
        break;
      
      case ErrorType.AUTHENTICATION:
        // Could trigger re-authentication flow
        logger.info('Authentication error, may need to refresh tokens', 'ErrorHandler');
        break;
      
      default:
        // No automatic recovery for other types
        break;
    }
  }
}

// Export singleton instance
export const errorHandler = ErrorHandler.getInstance();

// Convenience functions
export function handleError(error: Error | AppError, context?: ErrorContext): Promise<void> {
  return errorHandler.handleError(error, context);
}

export function createError(
  message: string,
  type: ErrorType = ErrorType.UNKNOWN,
  severity: ErrorSeverity = ErrorSeverity.MEDIUM,
  options?: {
    code?: string;
    context?: ErrorContext;
    recoverable?: boolean;
    userMessage?: string;
    technicalDetails?: any;
  }
): ApplicationError {
  return new ApplicationError(message, type, severity, options);
}

// Global error handlers
if (typeof window !== 'undefined') {
  window.addEventListener('error', (event) => {
    handleError(new Error(event.message), {
      component: 'window',
      metadata: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno
      }
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    handleError(new Error(event.reason), {
      component: 'promise',
      metadata: {
        reason: event.reason,
        promise: event.promise
      }
    });
  });
}